import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, User, BookOpen, BarChart3,
  ChevronDown, Edit2, Trash2, Plus, ClipboardList,
  CreditCard, Building2, SlidersHorizontal, Search, CheckCircle
} from "lucide-react";
import CreatePartyModal from "@/components/CreatePartyModal";
import InvoiceModal from "@/components/InvoiceModal";

const PartyDetailPage = () => {
  const navigate = useNavigate();
  const { partyId } = useParams();
  const [party, setParty] = useState(null);
  const [parties, setParties] = useState([]);
  const [activeTab, setActiveTab] = useState("transactions");
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceJobCard, setInvoiceJobCard] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const fetchParty = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/parties/${partyId}`);
      setParty(response.data);
    } catch {
      toast.error("Failed to fetch party details");
      navigate("/parties");
    } finally {
      setLoading(false);
    }
  }, [partyId, navigate]);

  const fetchAllParties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parties`);
      setParties(response.data);
    } catch (err) { console.error("Failed to fetch parties for sidebar:", err); }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setTxnLoading(true);
    try {
      const response = await axios.get(`${API}/parties/${partyId}/transactions`);
      setTransactions(response.data);
    } catch (err) { console.error("Failed to fetch transactions:", err); }
    finally { setTxnLoading(false); }
  }, [partyId]);

  useEffect(() => {
    fetchParty();
    fetchAllParties();
    fetchTransactions();
  }, [fetchParty, fetchAllParties, fetchTransactions]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this party?")) return;
    try {
      await axios.delete(`${API}/parties/${partyId}`);
      toast.success("Party deleted");
      navigate("/parties");
    } catch { toast.error("Failed to delete party"); }
  };

  const handleEditClose = (refresh) => {
    setEditModalOpen(false);
    if (refresh) fetchParty();
  };

  const handleCreateInvoice = (txn) => {
    setInvoiceJobCard({ id: txn.jobcard_id, job_card_number: txn.job_card_number, customer_name: txn.customer_name, type_of_service: txn.type_of_service, vehicle_details: '', status: txn.jobcard_status });
    setEditingInvoice(null);
    setInvoiceModalOpen(true);
  };

  const handleEditInvoice = (txn) => {
    setInvoiceJobCard({ id: txn.jobcard_id, job_card_number: txn.job_card_number, customer_name: txn.customer_name, type_of_service: txn.type_of_service, vehicle_details: '', status: txn.jobcard_status });
    setEditingInvoice({ id: txn.invoice_id, invoice_number: txn.invoice_number, amount: txn.invoice_amount, status: txn.invoice_status });
    setInvoiceModalOpen(true);
  };

  const handleMarkPaid = async (txn) => {
    try {
      await axios.put(`${API}/invoices/${txn.invoice_id}`, { status: 'Paid' });
      toast.success("Invoice marked as Paid");
      fetchTransactions();
    } catch { toast.error("Failed to update invoice"); }
  };

  const handleInvoiceModalClose = (refresh) => {
    setInvoiceModalOpen(false);
    setInvoiceJobCard(null);
    setEditingInvoice(null);
    if (refresh) fetchTransactions();
  };

  const filteredSidebarParties = parties.filter(p => {
    if (!sidebarSearch.trim()) return true;
    const q = sidebarSearch.toLowerCase();
    return p.party_name.toLowerCase().includes(q) || (p.mobile_number && p.mobile_number.includes(q));
  });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-slate-500">Loading party details...</p></div>;
  }
  if (!party) return null;

  const tabs = [
    { id: "transactions", label: "Transactions", icon: ClipboardList },
    { id: "profile", label: "Profile", icon: User },
    { id: "ledger", label: "Ledger (Statement)", icon: BookOpen },
    { id: "itemwise", label: "Item Wise Report", icon: BarChart3 }
  ];

  const pendingTotal = transactions.reduce((sum, t) => t.invoice_status === 'Pending' ? sum + (t.invoice_amount || 0) : sum, 0);
  const paidTotal = transactions.reduce((sum, t) => t.invoice_status === 'Paid' ? sum + (t.invoice_amount || 0) : sum, 0);

  return (
    <div className="flex h-[calc(100vh-60px)]">
      {/* Left Sidebar */}
      <div className="w-56 border-r border-slate-200 flex flex-col bg-white shrink-0">
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input data-testid="sidebar-search" placeholder="Search Customer" value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)} className="pl-8 h-9 text-sm" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredSidebarParties.map((p) => (
            <button key={p.id} data-testid={`sidebar-party-${p.id}`} onClick={() => navigate(`/parties/${p.id}`)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
                p.id === partyId ? "bg-indigo-50 border-l-[3px] border-l-indigo-500" : "hover:bg-slate-50 border-l-[3px] border-l-transparent"}`}>
              <p className={`text-sm font-medium truncate ${p.id === partyId ? "text-indigo-700" : "text-slate-800"}`}>{p.party_name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{formatCurrency(p.opening_balance)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button data-testid="back-to-parties" variant="ghost" size="icon" onClick={() => navigate("/parties")} className="text-slate-600 hover:text-slate-800">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>{party.party_name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="edit-party-btn" variant="outline" size="sm" onClick={() => setEditModalOpen(true)} className="gap-1.5 text-slate-700">
                <Edit2 className="w-4 h-4" />Edit
              </Button>
              <Button data-testid="delete-party-btn" variant="ghost" size="icon" onClick={handleDelete} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-6">
          <div className="flex gap-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                  <Icon className="w-4 h-4" />{tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ========== TRANSACTIONS TAB ========== */}
          {activeTab === "transactions" && (
            <div>
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="border border-slate-200">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-slate-500 mb-1">Total Job Cards</p>
                    <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
                  </CardContent>
                </Card>
                <Card className="border border-green-200 bg-green-50/30">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-green-600 mb-1">Pending Collection</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(pendingTotal)}</p>
                  </CardContent>
                </Card>
                <Card className="border border-blue-200 bg-blue-50/30">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-blue-600 mb-1">Total Paid</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(paidTotal)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Transactions Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Job Card No</TableHead>
                      <TableHead className="font-semibold text-slate-700">Service</TableHead>
                      <TableHead className="font-semibold text-slate-700">JC Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Invoice No</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Amount</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txnLoading ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Loading transactions...</TableCell></TableRow>
                    ) : transactions.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12">
                        <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-slate-500">No job cards linked to this party yet</p>
                      </TableCell></TableRow>
                    ) : (
                      transactions.map((txn) => (
                        <TableRow key={txn.jobcard_id} data-testid={`txn-row-${txn.job_card_number}`} className="hover:bg-slate-50">
                          <TableCell className="font-semibold text-blue-600">{txn.job_card_number}</TableCell>
                          <TableCell className="text-slate-600">{txn.type_of_service}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              txn.jobcard_status === 'Delivered' ? 'bg-blue-100 text-blue-700'
                              : txn.jobcard_status === 'Completed' ? 'bg-green-100 text-green-700'
                              : txn.jobcard_status === 'In Progress' ? 'bg-purple-100 text-purple-700'
                              : 'bg-amber-100 text-amber-700'}`}>
                              {txn.jobcard_status}
                            </span>
                          </TableCell>
                          <TableCell>
                            {txn.invoice_number ? (
                              <span className="text-sm font-medium text-slate-800">{txn.invoice_number}</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {txn.invoice_id ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                txn.invoice_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {txn.invoice_status}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">No Invoice</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {txn.invoice_amount ? (
                              <span className="font-medium text-slate-900">{formatCurrency(txn.invoice_amount)}</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {txn.jobcard_status === 'Delivered' && !txn.invoice_id && (
                                <Button data-testid={`create-inv-${txn.job_card_number}`} size="sm" variant="outline"
                                  onClick={() => handleCreateInvoice(txn)}
                                  className="h-7 text-xs gap-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                                  <FileText className="w-3 h-3" />Invoice
                                </Button>
                              )}
                              {txn.invoice_id && txn.invoice_status === 'Pending' && (
                                <>
                                  <Button data-testid={`edit-inv-${txn.job_card_number}`} size="sm" variant="ghost"
                                    onClick={() => handleEditInvoice(txn)}
                                    className="h-7 text-xs text-slate-600 hover:bg-slate-100">
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button data-testid={`pay-inv-${txn.job_card_number}`} size="sm" variant="ghost"
                                    onClick={() => handleMarkPaid(txn)}
                                    className="h-7 text-xs text-green-600 hover:bg-green-50 gap-1">
                                    <CheckCircle className="w-3 h-3" />Paid
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* ========== PROFILE TAB ========== */}
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card data-testid="general-details-card" className="border border-slate-200">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                    <ClipboardList className="w-4 h-4 text-slate-500" />General Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    <div><p className="text-xs text-slate-500 mb-1">Party Name</p><p className="text-sm font-medium text-slate-900">{party.party_name}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Party Type</p><p className="text-sm font-medium text-slate-900">{party.party_type}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Mobile Number</p><p className="text-sm font-medium text-slate-900">{party.mobile_number || "-"}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Party Category</p><p className="text-sm font-medium text-slate-900">{party.party_category || "-"}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Email</p><p className="text-sm font-medium text-slate-900">{party.email || "-"}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Opening Balance</p><p className="text-sm font-medium text-slate-900"><span className="mr-0.5">&#8377;</span>{formatCurrency(party.opening_balance)}</p></div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="business-details-card" className="border border-slate-200">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                    <Building2 className="w-4 h-4 text-slate-500" />Business Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    <div><p className="text-xs text-slate-500 mb-1">GSTIN</p><p className="text-sm font-medium text-slate-900">{party.gstin || "-"}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">PAN Number</p><p className="text-sm font-medium text-slate-900">{party.pan_number || "-"}</p></div>
                    <div className="col-span-2"><p className="text-xs text-slate-500 mb-1">Billing Address</p><p className="text-sm font-medium text-slate-900 whitespace-pre-wrap">{party.billing_address || "-"}</p></div>
                    <div className="col-span-2"><p className="text-xs text-slate-500 mb-1">Shipping Address</p><p className="text-sm font-medium text-slate-900 whitespace-pre-wrap">{party.shipping_address || "-"}</p></div>
                    {party.shipping_address && (
                      <div className="col-span-2"><button data-testid="manage-shipping-link" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">Manage Shipping Addresses (1)</button></div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="credit-details-card" className="border border-slate-200">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                    <CreditCard className="w-4 h-4 text-slate-500" />Credit Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    <div><p className="text-xs text-slate-500 mb-1">Credit Period</p><p className="text-sm font-medium text-slate-900">{party.credit_period || "-"}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">Credit Limit</p><p className="text-sm font-medium text-slate-900">{party.credit_limit || "-"}</p></div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="custom-fields-card" className="border border-slate-200 bg-amber-50/40">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                    <SlidersHorizontal className="w-4 h-4 text-slate-500" />Custom Fields
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">Now you can add custom item fields like Colour, Size, ID Number, etc.</p>
                    <Button data-testid="add-custom-field-btn" variant="ghost" size="icon" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg h-10 w-10 shrink-0 ml-4">
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                  {party.custom_fields && party.custom_fields.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {party.custom_fields.map((cf) => (
                        <div key={`cf-${cf.field_name}`} className="flex items-center gap-3 text-sm">
                          <span className="text-slate-500">{cf.field_name}:</span>
                          <span className="font-medium text-slate-900">{cf.field_value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "ledger" && (
            <div>
              {/* Ledger = summary of all transactions */}
              <Card className="border border-slate-200 mb-4">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div><p className="text-xs text-slate-500">Total Invoiced</p><p className="text-lg font-bold text-slate-900"><span className="text-sm mr-0.5">&#8377;</span>{formatCurrency(pendingTotal + paidTotal)}</p></div>
                    <div><p className="text-xs text-green-600">Received</p><p className="text-lg font-bold text-green-700"><span className="text-sm mr-0.5">&#8377;</span>{formatCurrency(paidTotal)}</p></div>
                    <div><p className="text-xs text-amber-600">Outstanding</p><p className="text-lg font-bold text-amber-700"><span className="text-sm mr-0.5">&#8377;</span>{formatCurrency(pendingTotal)}</p></div>
                  </div>
                </CardContent>
              </Card>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold">Debit</TableHead>
                      <TableHead className="font-semibold">Credit</TableHead>
                      <TableHead className="font-semibold">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.filter(t => t.invoice_id).length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No ledger entries yet</TableCell></TableRow>
                    ) : (() => {
                      let balance = 0;
                      return transactions.filter(t => t.invoice_id).map((txn) => {
                        const isDebit = txn.invoice_status === 'Pending';
                        if (isDebit) balance += txn.invoice_amount;
                        else balance -= txn.invoice_amount;
                        return (
                          <TableRow key={txn.jobcard_id}>
                            <TableCell className="text-sm">{txn.submitted_date}</TableCell>
                            <TableCell className="text-sm">{txn.invoice_number} - {txn.job_card_number}</TableCell>
                            <TableCell className="text-sm text-red-600">{isDebit ? `₹${formatCurrency(txn.invoice_amount)}` : '-'}</TableCell>
                            <TableCell className="text-sm text-green-600">{!isDebit ? `₹${formatCurrency(txn.invoice_amount)}` : '-'}</TableCell>
                            <TableCell className="text-sm font-medium">{balance >= 0 ? '' : '-'}₹{formatCurrency(Math.abs(balance))}</TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {activeTab === "itemwise" && (
            <div className="text-center py-16">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-lg">Item Wise Report</p>
              <p className="text-slate-400 text-sm mt-1">Item-wise reports for this party will appear here</p>
            </div>
          )}
        </div>
      </div>

      <CreatePartyModal open={editModalOpen} onClose={handleEditClose} editingParty={party} />
      <InvoiceModal open={invoiceModalOpen} onClose={handleInvoiceModalClose} jobCard={invoiceJobCard} existingInvoice={editingInvoice} />
    </div>
  );
};

export default PartyDetailPage;
